import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { missingLineCells, type ActionKey, type DocumentDef, type DocumentStatus, type TabKey, type Violation } from '@project/contracts'
import { Button, Field, MenuButton, Modal, StatusMark, toast, useFieldContext } from '../design/index.js'
import { PrintMenu } from './PrintMenu.js'
import { EmailDialog } from './EmailDialog.js'
import { useApi, type DocumentDetail, type LineRow } from '../app/api.js'
import { Link } from '../app/Link.js'
import { navigate, paths, registerDirtyProbe, watchLeave } from '../app/router.js'
import { useDocumentByRoute } from './queries.js'
import { useDeniedSpecialActions } from './special-action-gate.js'
import { FormTab } from './FormTab.js'
import { SummaryTab } from './SummaryTab.js'
import { RelatedTab } from './RelatedTab.js'
import { DriveTab } from './DriveTab.js'
import { ProjectDrivePage } from '../pages/ProjectDrivePage.js'
import { RevisionsTab } from './RevisionsTab.js'
import { TeamBoard } from './TeamBoard.js'
import { ProjectTeam } from './ProjectTeam.js'
import { OrderInsights, type InsightKind } from './OrderInsights.js'
import { RestateDialog } from './RestateDialog.js'
import { ExceptionDialog } from './ExceptionDialog.js'
import { ErrorBlock, Loading, NoticeBlock, messageFor } from './feedback.js'
import { useDocumentExtras } from './document-extras.js'

const TAB_LABELS: Record<TabKey, string> = {
  summary: 'Summary',
  related: 'Related Docs',
  revisions: 'Revisions',
  form: 'Form',
  drive: 'Project Drive',
}

const ACTION_LABELS: Record<ActionKey, string> = {
  save: 'Save',
  submit: 'Submit for approval',
  approve: 'Approve',
  reject: 'Return for changes',
  cancel: 'Cancel document',
  update: 'Update',
  print: 'Print',
  clone: 'Copy',
  email: 'Send by email',
  resync: 'Re-sync to the accounting system',
  stopSync: 'Stop syncing to the accounting system',
  changeCurrency: 'Change the currency',
  changeTaxUnit: 'Change the tax unit',
  raBill: 'Download the RA bill',
  splitBillable: 'Split into two challans',
  preClose: 'Pre close',
  undoPreClose: 'Reopen',
}

/**
 * The actions a header leads with. Everything else the document offers waits
 * in the More menu, so the header stays one row however many a type allows.
 */
const LEADING: ReadonlySet<ActionKey> = new Set<ActionKey>(['save', 'submit', 'approve', 'reject', 'update', 'print'])
const PRIMARY: ReadonlySet<ActionKey> = new Set<ActionKey>(['save', 'submit', 'approve'])

/**
 * Which actions make sense right now. The definition decides which exist.
 *
 * `approvesDraft`: the viewer may submit and sign the first stage in one step
 * (WF-002), so Approve is offered on the draft beside Submit.
 */
export const isOffered = (
  action: ActionKey, status: DocumentStatus, editing: boolean, preClosed = false, approvesDraft = false,
): boolean => {
  switch (action) {
    // Closing what is left short, and undoing that, are decisions about an
    // approved document; only something already closed can be reopened.
    case 'preClose':
      return status === 'APPROVED' && !editing
    case 'undoPreClose':
      return status === 'APPROVED' && !editing && preClosed
    case 'save':
      return editing
    case 'submit':
      return status === 'DRAFT'
    case 'approve':
      return status === 'IN_REVIEW' || (status === 'DRAFT' && approvesDraft)
    case 'reject':
      return status === 'IN_REVIEW'
    case 'update':
      return status === 'APPROVED' && !editing
    case 'cancel':
      return status !== 'CANCELLED'
    case 'print':
    case 'clone':
      return true
    // An order goes out once it has been approved; sending a draft to a
    // supplier is how an unapproved price becomes a commitment.
    case 'email':
      return status === 'APPROVED'
    // Nothing reaches the books before it is approved, so there is nothing to
    // chase before then either (docs/02 §4e).
    case 'resync':
    case 'stopSync':
      return status === 'APPROVED'
    // On an approved document a restatement; on a draft a quick change from
    // the Action menu, as the reference offers it there too (WF-002).
    case 'changeCurrency':
    case 'changeTaxUnit':
      return status === 'APPROVED' || status === 'DRAFT'
    /*
      Available on a draft as well as an approved document, on purpose: the
      annexure taken from a billing request is what goes to the customer for
      acknowledgement BEFORE anything is invoiced, which is the whole reason
      the vendor puts the download there (docs/video-notes/06 §6). Only a
      cancelled document has nothing to annexe.
    */
    case 'raBill':
      return status !== 'CANCELLED'
    /*
      Only on a draft, and only there.

      Moving lines onto a second challan changes what each one allocates, so
      it happens before anything is submitted — afterwards the two challans
      would have to be cancelled and re-raised, which is the situation this
      exists to avoid (docs/video-notes/06 §2).
    */
    case 'splitBillable':
      return status === 'DRAFT'
  }
}

/**
 * What a toast says once an action has gone through (PROJ2-022, PROJ3-002),
 * in the reference's words. Cancel is worded by what it was: a draft that was
 * never numbered is discarded, anything else cancelled.
 */
export const actionToast = (action: ActionKey, discarded = false): string | null => {
  switch (action) {
    case 'save': return 'The document has been saved successfully!'
    case 'submit': return 'The document has been submitted successfully!'
    case 'approve': return 'The document has been approved successfully!'
    case 'reject': return 'The document has been returned for changes.'
    case 'cancel': return discarded ? 'The document has been discarded successfully!' : 'The document has been cancelled successfully!'
    case 'update': return 'The document has been reopened for changes.'
    default: return null
  }
}

const DESTRUCTIVE: ReadonlySet<ActionKey> = new Set<ActionKey>(['cancel', 'reject'])
/** Actions that stop to ask why before they run. */
const CONFIRMED: ReadonlySet<ActionKey> = new Set<ActionKey>(['cancel', 'reject', 'update', 'preClose', 'undoPreClose'])
/** …and the ones where the answer is required, because it is kept on the record. */
const NEEDS_REASON: ReadonlySet<ActionKey> = new Set<ActionKey>(['reject', 'update', 'preClose'])

/** A textarea that still answers to the Field label sitting above it. */
const ReasonBox = ({ value, onChange }: { value: string; onChange: (next: string) => void }) => {
  const field = useFieldContext()
  return (
    <textarea
      id={field?.id}
      className="control"
      style={{ minHeight: 96, resize: 'vertical' }}
      value={value}
      autoFocus
      aria-required={field?.required}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  )
}

/** A date picker that answers to the Field label above it, like ReasonBox. */
const DateBox = ({ value, onChange }: { value: string; onChange: (next: string) => void }) => {
  const field = useFieldContext()
  return (
    <input
      id={field?.id}
      type="date"
      className="control"
      value={value}
      aria-required={field?.required}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  )
}

export interface DocumentPageProps {
  readonly companyId: string
  readonly docRoute: string
  readonly id: string
}

/** The types whose rate cells open the purchase price history at that line (PUR-039). */
const HISTORY_TYPES: ReadonlySet<string> = new Set(['purchase_order', 'subcontract_order', 'supplier_quotation'])

export const DocumentPage = ({ companyId, docRoute, id }: DocumentPageProps) => {
  const api = useApi()
  const queryClient = useQueryClient()
  const resolved = useDocumentByRoute(docRoute)
  const def: DocumentDef | undefined = resolved.def

  const doc = useQuery({
    queryKey: ['doc', api.companyId, def?.key, id],
    queryFn: () => api.document(def?.key ?? '', id),
    enabled: Boolean(def?.key),
  })
  const deniedActions = useDeniedSpecialActions(def?.key)

  const [tab, setTab] = useState<TabKey>('summary')
  const [editing, setEditing] = useState(false)
  const [lines, setLines] = useState<readonly LineRow[]>([])
  const [linesDirty, setLinesDirty] = useState(false)
  const [exceptions, setExceptions] = useState<{
    violations: readonly Violation[]
    historic: boolean
    preview?: boolean
    /** Which action an override resubmits: Submit, or Approve on a draft. */
    via?: 'submit' | 'approve'
  } | null>(null)
  /** A grid to scroll to once the Form tab has drawn it (BILL-018). */
  const [anchor, setAnchor] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<ActionKey | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  // Reference: Revision Details — Revision Date* beside the remarks, both
  // asked afresh each time (PROJ1-020): Done waits for the two of them.
  const [revisionDate, setRevisionDate] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [emailing, setEmailing] = useState(false)
  const [synced, setSynced] = useState<string | null>(null)
  const [insight, setInsight] = useState<InsightKind | null>(null)
  /** The line a per-line price history icon opened it on (PUR-039). */
  const [insightLine, setInsightLine] = useState<string | undefined>(undefined)
  const [restating, setRestating] = useState<'changeCurrency' | 'changeTaxUnit' | null>(null)

  const form = useForm<Record<string, unknown>>({ defaultValues: {} })
  const { reset } = form

  const data = doc.data
  useEffect(() => {
    if (!data) return
    reset(data.fields)
    setLines(data.lines)
    setLinesDirty(false)
    setEditing(data.documentStatus === 'DRAFT')
  }, [data, reset])

  const dirty = form.formState.isDirty || linesDirty
  useEffect(() => registerDirtyProbe(() => dirty), [dirty])

  /*
    A create opened and left without saving leaves nothing behind (BILL-040):
    the reference persists nothing until Save. A draft never saved since the
    create dialog made it, with no lines, is discarded once its page is left.
  */
  const untouched = data !== undefined && data.documentStatus === 'DRAFT' && data.version === 0
    && data.documentNo === null && data.lines.length === 0
  useEffect(() => {
    if (!untouched || !def || !data) return undefined
    const key = def.key
    const docId = data.id
    return watchLeave(window.location.hash, () => {
      void api.discardDraft(key, docId)
        .then(() => queryClient.invalidateQueries({ queryKey: ['list', api.companyId, key] }))
        .catch(() => undefined)
    })
  }, [untouched, def, data, api, queryClient])

  const onLinesChange = useCallback((next: readonly LineRow[]) => {
    setLines(next)
    setLinesDirty(true)
  }, [])

  /** `quiet`: saved on the way to another action, whose own toast says what happened. */
  const save = useMutation({
    mutationFn: async (_opts?: { quiet?: boolean }) => {
      if (!def || !data) throw new Error('The document is not loaded.')
      return api.save(def.key, data.id, { version: data.version, fields: form.getValues(), lines })
    },
    onSuccess: (saved, opts) => {
      queryClient.setQueryData(['doc', api.companyId, def?.key, id], saved)
      reset(saved.fields)
      setLines(saved.lines)
      setLinesDirty(false)
      setActionError(null)
      if (!opts?.quiet) toast(actionToast('save') ?? '')
      // Numbered by this save (PROJ2-022): the reference lands on the Summary.
      if (def?.numberOnSave && !data?.documentNo && saved.documentNo) setTab('summary')
    },
    onError: (err) => setActionError(messageFor(err)),
  })

  const act = useMutation({
    mutationFn: async ({ action, acknowledged, reason, extra }: {
      action: ActionKey; acknowledged?: readonly string[]; reason?: string; extra?: Record<string, unknown>
    }) => {
      if (!def || !data) throw new Error('The document is not loaded.')
      return api.act(def.key, data.id, action, acknowledged, reason, extra)
    },
    onSuccess: (result, variables) => {
      setActionError(null)
      if (result.ok) {
        setExceptions(null)
        const said = actionToast(variables.action, variables.action === 'cancel' && extras.cancelLabel === 'Discard')
        if (said) toast(said)
        void queryClient.invalidateQueries({ queryKey: ['doc', api.companyId, def?.key, id] })
        void queryClient.invalidateQueries({ queryKey: ['list', api.companyId, def?.key] })
      } else {
        setExceptions({
          violations: result.violations, historic: false,
          via: variables.action === 'approve' ? 'approve' : 'submit',
        })
      }
    },
    onError: (err) => setActionError(messageFor(err)),
  })

  /**
   * The accounting system, from the document (docs/02 §4e).
   *
   * Whoever is looking at the invoice is who notices it never reached the
   * books; making them find it again on an admin screen is how it stays
   * unnoticed for a month.
   */
  const sync = useMutation({
    mutationFn: async (what: 'resync' | 'stopSync') => {
      if (!data) throw new Error('The document is not loaded.')
      return what === 'resync' ? api.requeueDocument(data.id) : api.stopDocument(data.id)
    },
    onSuccess: (_rows, what) => {
      setActionError(null)
      setSynced(what === 'resync'
        ? 'Queued for the accounting system.'
        : 'Held back from the accounting system.')
      void queryClient.invalidateQueries({ queryKey: ['doc', api.companyId, def?.key, id] })
    },
    onError: (err) => setActionError(messageFor(err)),
  })

  const email = useMutation({
    mutationFn: async () => {
      if (!data) throw new Error('The document is not loaded.')
      return api.emailDocument(data.id)
    },
    onSuccess: (result) => {
      setActionError(null)
      setSentTo(result.queued ? result.to : null)
      void queryClient.invalidateQueries({ queryKey: ['doc', api.companyId, def?.key, id] })
    },
    onError: (err) => setActionError(messageFor(err)),
  })

  const split = useMutation({
    mutationFn: async () => {
      if (!def || !data) throw new Error('The document is not loaded.')
      return api.splitBillable(def.key, data.id)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['list', api.companyId, def?.key] })
    },
    onError: (err) => setActionError(messageFor(err)),
  })

  const clone = useMutation({
    mutationFn: async () => {
      if (!def || !data) throw new Error('The document is not loaded.')
      return api.clone(def.key, data.id)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['list', api.companyId, def?.key] })
    },
    onError: (err) => setActionError(messageFor(err)),
  })

  const busy = save.isPending || act.isPending || clone.isPending || email.isPending || sync.isPending

  // Save NT-BOQ, Update, Download bills, Mark as closed, Delete (document-extras.tsx).
  const extras = useDocumentExtras({ def, doc: data, editing, form, companyId, onError: setActionError })

  const closeConfirm = useCallback(() => {
    setConfirming(null)
    setRejectReason('')
    setRevisionDate('')
  }, [])

  /**
   * Every required line cell filled (`LineColumnDef.required`)? Says which is
   * missing, in the error banner, when one is not.
   */
  const linesComplete = (): boolean => {
    if (!def) return true
    const missing = missingLineCells(def, lines)
    if (missing.length === 0) return true
    setActionError(missing.length === 1
      ? missing[0]!.message
      : `${missing[0]!.message} (and ${missing.length - 1} more)`)
    return false
  }

  const run = async (action: ActionKey, reason?: string) => {
    if (!def || !data) return
    setActionError(null)
    if (action === 'update') {
      // Reopening is the server's decision, not the browser's: it writes the
      // signed version to the revision log with the reason, and returns the
      // document to draft. Unlocking the form here alone left a Save that the
      // server then refused, because the document was still approved.
      const result = await act.mutateAsync({ action, reason, extra: { revisionDate } })
      if (result.ok) setTab('form')
      return
    }
    if (action === 'clone') {
      // The server does the copying — header and lines, without the number or
      // the approvals — and the new draft is what opens.
      const copy = await clone.mutateAsync()
      navigate(paths.document(companyId, docRoute, copy.id))
      return
    }
    if (action === 'save') {
      const valid = await form.trigger()
      if (!valid || !linesComplete()) {
        setTab('form')
        return
      }
      save.mutate({})
      return
    }
    if (action === 'submit' || (action === 'approve' && data.documentStatus === 'DRAFT')) {
      const valid = await form.trigger()
      if (!valid || !linesComplete()) {
        setTab('form')
        return
      }
      if (dirty) await save.mutateAsync({ quiet: true })
      act.mutate({ action })
      return
    }
    if (action === 'changeCurrency' || action === 'changeTaxUnit') {
      // On a draft the change is made to what is saved, so save first.
      if (data.documentStatus === 'DRAFT' && dirty) await save.mutateAsync({ quiet: true })
      setRestating(action)
      return
    }
    if (action === 'resync' || action === 'stopSync') {
      await sync.mutateAsync(action)
      return
    }
    if (action === 'email') {
      // Which wording and which printed layout: chosen in the dialog, then
      // queued rather than sent inline — an SMTP timeout must not fail the
      // click, and the outbox is where a bounce is chased afterwards.
      setEmailing(true)
      return
    }
    if (action === 'splitBillable') {
      // The lines that disagreed with the header move to a new draft, and that
      // draft is what opens: it is the one still needing a look before it goes
      // out, and the original is already correct.
      if (dirty) await save.mutateAsync({ quiet: true })
      const other = await split.mutateAsync()
      navigate(paths.document(companyId, docRoute, other.id))
      return
    }
    if (action === 'raBill') {
      // A download, not a page: the annexure is edited offline before it goes
      // to the customer, so what leaves here is the workbook itself.
      if (def && data) window.location.assign(api.raBillUrl(def.key, data.id))
      return
    }
    if (action === 'print') {
      // Opened rather than fetched: the browser's own print dialog is what
      // turns it into a PDF, and it needs the page in a window to do that.
      if (def && data) window.open(api.printUrl(def.key, data.id), '_blank', 'noopener')
      return
    }
    act.mutate({ action, reason })
  }

  /*
    A fresh draft shows only what it has (PROJ2-022: Summary · Form · Project
    Drive): no Revisions before it has ever been revised, and — for a project,
    which nothing is raised against until it is approved — no Related.
  */
  const fresh = data !== undefined && data.documentStatus === 'DRAFT' && data.revisionNo === 0
  const tabs = useMemo(
    () => (def?.tabs ?? []).filter((t) => !(fresh && (t === 'revisions' || (t === 'related' && def?.key === 'project')))),
    [def, fresh],
  )

  // Once the Form tab is drawn, bring the grid a line count pointed at into view.
  useEffect(() => {
    if (!anchor || tab !== 'form') return
    const el = document.getElementById(anchor)
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setAnchor(null)
  }, [anchor, tab])
  // A definition need not offer every tab, so never leave the strip on one it lacks.
  const activeTab: TabKey = tabs.includes(tab) ? tab : (tabs[0] ?? 'summary')

  if (resolved.missing) {
    return (
      <div className="page">
        <h1 className="page-title">Page not found</h1>
        <p className="page-subtitle">There is no document type at “{docRoute}”.</p>
      </div>
    )
  }
  if (resolved.error) return <div className="page"><ErrorBlock error={resolved.error} /></div>
  if (!def || doc.isLoading) return <div className="page"><Loading /></div>
  if (doc.error) return <div className="page"><ErrorBlock error={doc.error} /></div>
  if (!data) return <div className="page"><Loading /></div>

  const readOnly = !editing || data.documentStatus === 'CANCELLED'
  const preClosed = data.lines.some((l) => Number(l['preClosedQty'] ?? 0) > 0)
  const signed = data.documentStatus !== 'DRAFT'
  const offered = def.actions.filter((action) =>
    isOffered(action, data.documentStatus, editing, preClosed, data.canApproveDraft === true)
    && !extras.hidden.has(action) && !deniedActions.has(action))
  const team = def.key === 'project'
    ? <ProjectTeam projectId={data.project?.masterId ?? data.id} title="Project Team" locked={data.documentStatus !== 'DRAFT'} />
    : null
  const teamPlaced = team !== null && (def.formOrder ?? []).includes('slot:projectTeam')
  // An approved project's NT BOQ sits before its cost and value summaries (PROJ2-001).
  const footerPlaced = extras.formFooter !== null && (def.formOrder ?? []).includes('slot:ntBoq')
  const slots = {
    ...(teamPlaced ? { projectTeam: team } : {}),
    ...(footerPlaced ? { ntBoq: extras.formFooter } : {}),
  }
  // The reference's wording for throwing away a draft (PROJ3-002).
  const discarding = confirming === 'cancel' && extras.cancelLabel === 'Discard'
  const labelOf = (action: ActionKey): string => (action === 'cancel' ? extras.cancelLabel : ACTION_LABELS[action])

  return (
    <div className="page">
      <p className="crumb">
        <Link to={paths.list(companyId, docRoute)}>{def.labelPlural}</Link>
      </p>
      <div className="doc-head">
        <div>
          <h1 className="page-title">
            {data.documentNo ?? `New ${def.label.toLowerCase()}`}
            {/* Reference breadcrumb: "Revised 3 time(s)" after the status (PROJ2-021). */}
            {data.revisionNo > 0 ? (
              <span className="chip" style={{ marginLeft: 12, verticalAlign: 'middle' }}>
                Revised {data.revisionNo} time(s)
              </span>
            ) : null}
          </h1>
          {data.headline ? <p className="page-subtitle">{data.headline}</p> : null}
        </div>
        <div className="doc-actions">
          <MenuButton
            disabled={busy}
            items={[
              ...offered
                .filter((action) => !LEADING.has(action))
                .map((action) => ({
                  key: action,
                  label: labelOf(action),
                  danger: action === 'cancel',
                  onSelect: () => (CONFIRMED.has(action) ? setConfirming(action) : void run(action)),
                })),
              ...extras.items,
              { key: 'close', label: 'Close', onSelect: () => navigate(paths.list(companyId, docRoute)) },
            ]}
          />
          {offered
            .filter((action) => LEADING.has(action))
            .map((action) => action === 'print' ? (
              // Print, with every active layout for the type a click away (PRN-002).
              <PrintMenu key={action} docType={def.key} documentId={data.id} disabled={busy} label={ACTION_LABELS.print} />
            ) : (
              <Button
                key={action}
                kind={PRIMARY.has(action) ? 'primary' : 'ghost'}
                disabled={busy}
                onClick={() => (CONFIRMED.has(action) ? setConfirming(action) : void run(action))}
              >
                {ACTION_LABELS[action]}
              </Button>
            ))}
        </div>
      </div>

      {actionError ? <div style={{ marginTop: 16 }}><NoticeBlock tone="bad">{actionError}</NoticeBlock></div> : null}
      {sentTo ? (
        <div style={{ marginTop: 16 }}>
          <NoticeBlock tone="good">
            Queued for {sentTo}. Track it under Settings → Email outbox.
          </NoticeBlock>
        </div>
      ) : null}
      {synced ? (
        <div style={{ marginTop: 16 }}>
          <NoticeBlock tone="good">{synced}</NoticeBlock>
        </div>
      ) : null}

      <nav className="tabstrip" aria-label="Document sections">
        {tabs.map((key) => (
          <button
            key={key}
            type="button"
            className={key === activeTab ? 'tabstrip__tab tabstrip__tab--active' : 'tabstrip__tab'}
            aria-current={key === activeTab ? 'page' : undefined}
            onClick={() => setTab(key)}
          >
            {TAB_LABELS[key]}
          </button>
        ))}
      </nav>

      <div className="sheet">
        <div className={signed ? 'sheet__rule' : 'sheet__rule sheet__rule--draft'} aria-hidden="true" />
        <div className="sheet__margin">
          <span className="sheet__margin-rev">Rev {data.revisionNo}</span>
          <StatusMark status={data.documentStatus} discarded={data.documentStatus === 'CANCELLED' && (!data.documentNo || data.neverSubmitted === true)} />
        </div>
        <div className="sheet__body">
          {activeTab === 'summary' ? (
            <>
              <SummaryTab
                doc={data}
                onOpenExceptions={(violations, preview) => setExceptions({ violations, historic: !preview, preview: preview === true })}
                onOpenInsight={setInsight}
                onOpenGrid={(section) => {
                  setAnchor(`grid-${section}`)
                  setTab('form')
                }}
              />
              {/* What happened to this document, and what people said about it. */}
              <TeamBoard documentId={data.id} />
            </>
          ) : null}
          {activeTab === 'related' ? <RelatedTab def={def} doc={data} companyId={companyId} /> : null}
          {activeTab === 'revisions' ? (
            <RevisionsTab
              doc={data}
              docLabel={def.label}
              onRevise={
                def.actions.includes('update') && isOffered('update', data.documentStatus, editing)
                  ? () => setConfirming('update')
                  : undefined
              }
            />
          ) : null}
          {activeTab === 'form' ? (
            <>
              <FormTab
                def={def}
                form={form}
                status={data.documentStatus}
                fieldLabels={data.fieldLabels ?? {}}
                lines={extras.formLines(lines)}
                onLinesChange={onLinesChange}
                readOnly={readOnly}
                approvedEditable={extras.approvedEditable}
                dirty={dirty}
                documentId={data.id}
                {...(HISTORY_TYPES.has(def.key)
                  ? { onRateHistory: (lineId: string) => { setInsightLine(lineId); setInsight('history') } }
                  : {})}
                doc={data}
                {...(offered.includes('changeTaxUnit') ? { onChangeTaxUnit: () => void run('changeTaxUnit') } : {})}
                {...(offered.includes('changeCurrency') ? { onChangeCurrency: () => void run('changeCurrency') } : {})}
                slots={slots}
              />
              {footerPlaced ? null : extras.formFooter}
              {/*
                The project's own team belongs on the project (docs/05 §Project
                Team). It is not a grid of lines — the people are records of
                their own — so it sits below the form rather than inside it,
                unless the definition's `formOrder` places it ('slot:projectTeam').
              */}
              {teamPlaced ? null : team}
            </>
          ) : null}
          {activeTab === 'drive' ? (
            // A project's drive tab IS its Project Drive (reference): files,
            // collections and team, with Switch project on the drive page.
            def.key === 'project' && data.project?.masterId ? (
              <>
                <p className="helper" style={{ marginBottom: 12 }}>
                  <Link to={paths.drive(companyId, { project: data.project.masterId })}>Switch project</Link>
                </p>
                <ProjectDrivePage
                  companyId={companyId}
                  projectId={data.project.masterId}
                  projectName={String(data.fields['name'] ?? data.documentNo ?? '')}
                />
              </>
            ) : <DriveTab docKey={def.key} docId={data.id} />
          ) : null}
        </div>
      </div>

      {extras.dialog}

      {emailing && def && data ? (
        <EmailDialog
          docType={def.key}
          documentId={data.id}
          onClose={() => setEmailing(false)}
          onSent={(result) => {
            setEmailing(false)
            setActionError(null)
            setSentTo(result.queued ? result.to : null)
            void queryClient.invalidateQueries({ queryKey: ['doc', api.companyId, def.key, id] })
          }}
        />
      ) : null}
      {restating ? (
        <RestateDialog
          docKey={def.key}
          doc={data}
          what={restating}
          onClose={() => setRestating(null)}
          onDone={(saved) => {
            queryClient.setQueryData(['doc', api.companyId, def.key, id], saved)
            setRestating(null)
          }}
        />
      ) : null}

      {insight ? (
        <OrderInsights
          docKey={def.key}
          documentId={data.id}
          kind={insight}
          {...(insightLine ? { lineId: insightLine } : {})}
          onClose={() => { setInsight(null); setInsightLine(undefined) }}
        />
      ) : null}

      {exceptions ? (
        <ExceptionDialog
          violations={exceptions.violations}
          historic={exceptions.historic}
          preview={exceptions.preview === true}
          busy={busy}
          onFix={() => {
            setExceptions(null)
            if (!exceptions.historic && !exceptions.preview) setTab('form')
          }}
          onOverride={
            exceptions.historic || exceptions.preview
              ? undefined
              : () => act.mutate({ action: exceptions.via ?? 'submit', acknowledged: exceptions.violations.map((v) => v.rule) })
          }
        />
      ) : null}

      {confirming ? (
        <Modal
          title={confirming === 'update' ? 'Revision Details'
            : discarding ? 'Discarding document?' : `${labelOf(confirming)}?`}
          tone={DESTRUCTIVE.has(confirming) ? 'warn' : 'plain'}
          onClose={closeConfirm}
          actions={
            <>
              <Button kind="ghost" onClick={closeConfirm}>{discarding ? 'No, don’t discard it' : 'Keep it as it is'}</Button>
              <Button
                kind="primary"
                disabled={busy || (NEEDS_REASON.has(confirming) && rejectReason.trim() === '')
                  || (confirming === 'update' && revisionDate === '')}
                onClick={() => {
                  const action = confirming
                  const reason = rejectReason.trim()
                  closeConfirm()
                  void run(action, NEEDS_REASON.has(action) ? reason : undefined)
                }}
              >
                {discarding ? 'Yes, discard it' : confirming === 'update' ? 'Done' : labelOf(confirming)}
              </Button>
            </>
          }
        >
          {confirming === 'update' ? null : <p>
            {confirming === 'cancel' && extras.cancelLabel === 'Discard'
              ? 'Do you want to discard this document? It was never submitted; once discarded it can be deleted.'
              : confirming === 'cancel'
              ? 'The document will be marked cancelled. Anything allocated against it is released.'
              : confirming === 'preClose'
                ? 'Whatever is still open on every line is closed off. It stops showing as pending in work lists, pickers and reports. Nothing received so far changes, and Reopen undoes it.'
                : confirming === 'undoPreClose'
                  ? 'What was closed off is open again, and shows as pending until it is received or closed.'
                : 'The document goes back to the person who raised it, and the approval starts again at the first step.'}
          </p>}
          {confirming === 'preClose' ? (
            <div style={{ marginTop: 18 }}>
              <Field label="Why is the rest being closed?" required>
                <ReasonBox value={rejectReason} onChange={setRejectReason} />
              </Field>
            </div>
          ) : null}
          {confirming === 'update' ? (
            <div>
              <Field label="Revision Date" required>
                <DateBox value={revisionDate} onChange={setRevisionDate} />
              </Field>
              <div style={{ height: 14 }} />
              <Field label="Remarks for Revision" required>
                <ReasonBox value={rejectReason} onChange={setRejectReason} />
              </Field>
              <p className="muted" style={{ marginTop: 10 }}>
                The approved version is kept as revision {data.revisionNo + 1} with these remarks; the document goes
                back to draft and needs approval again before it counts.
              </p>
            </div>
          ) : null}
          {confirming === 'reject' ? (
            <div style={{ marginTop: 18 }}>
              <Field label="Why are you sending it back?" required>
                <ReasonBox value={rejectReason} onChange={setRejectReason} />
              </Field>
              <p className="muted" style={{ marginTop: 10 }}>
                They see this on the document, so say what needs changing.
              </p>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </div>
  )
}
